import { CharacterFlow, NotificationType, useCharacter, useLorebook, useTranslation } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { CHARACTER_VISIBILITY } from "@next-platform/client-common-core/utils/sharedTypes";
import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { Lorebook } from "@next-platform/packages-ts-types";
import { Spacer, Spinner } from "@nextui-org/react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PageLayout, PageSize } from "@/components";
import TextStyles from "@/components/common/TextStyles";
import { Grid } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag, useGroupChatDrafts } from "@/hooks";
import useToast from "@/hooks/useToast";
import CreateCharacterHeroSection from "@/pages/CreateCharacter/v2/components/CreateCharacterHeroSection";
import CreateCharacterLockedOverlay from "@/pages/CreateCharacter/v2/components/CreateCharacterLockedOverlay";
import CreateCharacterPerksSection from "@/pages/CreateCharacter/v2/components/CreateCharacterPerksSection";
import CreateGroupForm from "@/pages/Groupchat/components/CreateGroupForm";
import buildGroupChatRequest from "@/pages/Groupchat/utils/buildGroupChatRequest";
import useAuth from "@/services/useAuth";
import { IDraftGroupCharacter } from "@/types";
import { cn } from "@/utils";

// This component follows the footsteps of its cousin, the create chatbot component V2.
// However, instead of creating a charatcter it creates a room and involves already existing bots that user may add to common conversation he/she constructs
// many features from create chatbot V2 were reused, however, in spite of functional similarities, the API endpoints and data shapes of this component are meant to be different
// To see this component, enable Group chat feature flag

export function Groupchat() {
  const {
    onUserCreateCharacter,
    isLoading: isCharacterLoading,
    loadingType: characterLoadingType,
    errorMessage: submitError,
    errorCode,
  } = useCharacter();
  const { onUserAttachLorebook } = useLorebook();
  const applicationId = basicAppConfig.applicationId;
  const { draftId } = useParams();
  const { isAuthenticated, isLoading } = useAuth();
  const { getDraftById } = useGroupChatDrafts();
  const { displayToast } = useToast();
  const { hasFeatureFlag: hasGroupLorebookFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.RELEASE_GROUP_LOREBOOK_ATTACHMENT_WEB);

  const storedData = draftId ? getDraftById(draftId) : null;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stateIsSubmitForManualReview, setStateIsSubmitForManualReview] = useState(false);
  const isSubmitting = isCharacterLoading && characterLoadingType === CharacterFlow.CREATE_CHARACTER;

  const attachSelectedLorebookToGroup = async ({ groupId, currentLorebooks }: { groupId: string; currentLorebooks: Lorebook[] }) => {
    if (!hasGroupLorebookFeatureFlag) return;

    const selectedLorebookId = currentLorebooks[0]?.id;
    if (!selectedLorebookId) return;

    const attachResult = await onUserAttachLorebook(selectedLorebookId, { characterId: groupId });
    if (!attachResult?.error) return;

    displayToast({ message: t("chatbot:toast.error.attachLorebook"), type: NotificationType.error });
  };

  const onSave = async (props: IDraftGroupCharacter, currentLorebooks: Lorebook[] = []) => {
    const requestProps = buildGroupChatRequest({
      draftGroupCharacter: props,
      includeClearedGreetingCharacter: false,
      includeType: true,
      submitForManualReview: stateIsSubmitForManualReview,
    });

    const { data: savedGroupChat, error, errorPayload } = await onUserCreateCharacter(requestProps);

    if (savedGroupChat?.id) {
      await attachSelectedLorebookToGroup({ groupId: savedGroupChat.id, currentLorebooks });
      navigate(`/chat/${savedGroupChat.id}`);
    }

    if (errorPayload?.["data"]?.submitForManualReview === true) {
      setStateIsSubmitForManualReview(true);
    }

    return { ["data"]: savedGroupChat, error };
  };

  if (isLoading) {
    return <Spinner className={TextStyles.spinner} />;
  }

  return (
    <PageLayout size={PageSize.Medium}>
      {!isAuthenticated && (
        <>
          <CreateCharacterHeroSection />
          <CreateCharacterPerksSection />
        </>
      )}
      <div
        className={cn(
          "flex flex-col relative bg-gray-1 dark:bg-gray-3 p-lg max-mob:p-md rounded-[16px] border border-solid border-gray-5",
          !isAuthenticated && "py-16",
        )}
      >
        {!isAuthenticated ? <CreateCharacterLockedOverlay /> : null}
        <Spacer y={0.5} />
        <Grid className="flex justify-between flex-row max-mob:flex-col">
          <Text variant={TextVariants.HEADING_5_BOLD}>
            <Trans i18nKey="createGroup:page.title" />
          </Text>
          <LocalizedLink
            className="flex items-center text-link"
            target="_blank"
            to={EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.group_creation_guide}
            aria-label={t("common:creationGuide")}
          >
            <Trans i18nKey="common:creationGuide" />
          </LocalizedLink>
        </Grid>
        <Spacer y={0.5} />
        <CreateGroupForm
          key={draftId || "new"}
          id={storedData?.id || ""}
          name={storedData?.name || ""}
          avatarUrl={storedData?.avatar_url || ""}
          greeting={storedData?.greeting || ""}
          greetingCharacterId={storedData?.greeting_character_id}
          visibility={(storedData?.visibility as CHARACTER_VISIBILITY) || CHARACTER_VISIBILITY.PRIVATE}
          title={storedData?.title || ""}
          scenario={storedData?.scenario || ""}
          guidelines={false}
          tags={storedData?.tags || []}
          participants={storedData?.members}
          onSave={onSave}
          draftId={draftId || ""}
          isSubmitting={isSubmitting}
          submitError={submitError || ""}
          errorCode={errorCode}
          submitForManualReview={stateIsSubmitForManualReview}
          setIsSubmitForManualReview={setStateIsSubmitForManualReview}
          isAlreadyDraft={draftId ? true : false}
          isEdit={false}
          maxParticipants={10}
          lorebooks={storedData?.lorebooks || []}
        />
      </div>
    </PageLayout>
  );
}

export default Groupchat;
